import { useQuery } from '@tanstack/react-query';
import { api, type AccountHealthResult, type ApiError, type CustomerHealthResult } from '@whinerp/client';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Customer health (G-198): the score with its parts in words, and what had no
 * data - on the company, and as a list of who needs attention.
 */

const BAND_CLASS = { healthy: 'text-ink', watch: 'text-warning-text', 'at-risk': 'text-danger-text' } as const;

export function AccountHealthPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const enabled = useEntitlement('F-080').enabled;
  const health = useQuery<AccountHealthResult, ApiError>({
    queryKey: ['account-health', accountId],
    queryFn: () => api.accountHealth({ path: { accountId } }),
    enabled,
  });
  const view = health.data?.health;
  if (!enabled || !view) return null;
  return (
    <Panel data-region="account-health">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('custHealth.title')}</SectionLabel>
        <p className={`text-body ${view.band ? BAND_CLASS[view.band] : 'text-ink-meta'}`} data-health-band={view.band ?? 'none'}>
          {view.score === null ? t('custHealth.noData') : t('custHealth.score', { values: { score: String(view.score), band: t(`custHealth.band.${view.band!}`) } })}
        </p>
        <ul className="flex flex-col gap-1 text-dense">
          {view.signals.map((signal) => (
            <li key={signal.key} data-health-signal={signal.key} className="flex gap-2">
              <span className="w-24 text-ink-meta">{t(`custHealth.signal.${signal.key}`)}</span>
              <span className="text-ink">{signal.reason}</span>
            </li>
          ))}
        </ul>
        {view.missing.length > 0 ? (
          <p className="text-meta text-ink-meta" data-health-missing>
            {t('custHealth.missing', { values: { signals: view.missing.map((key) => t(`custHealth.signal.${key}`)).join(', ') } })}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

export function CustomerHealthPanel(): JSX.Element | null {
  const t = useT();
  const { can } = useSession();
  const enabled = useEntitlement('F-080').enabled && can('revenue.view');
  const list = useQuery<CustomerHealthResult, ApiError>({ queryKey: ['customer-health'], queryFn: () => api.customerHealth({}), enabled });
  if (!enabled || !list.data) return null;
  const attention = list.data.customers.filter((entry) => entry.band === 'at-risk' || entry.band === 'watch').slice(0, 10);
  return (
    <Panel data-region="customer-health">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('custHealth.listTitle')}</SectionLabel>
        {attention.length === 0 ? (
          <p className="text-body text-ink-meta" data-health-all-well>{t('custHealth.allWell', { values: { count: String(list.data.customers.length) } })}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-dense">
            {attention.map((entry) => {
              const worst = [...entry.signals].sort((a, b) => a.score - b.score)[0];
              return (
                <li key={entry.accountId} data-health-account={entry.accountId} className="flex flex-wrap gap-2">
                  <a className="text-ink underline" href={`/accounts/${entry.accountId}`}>{entry.name}</a>
                  <span className={entry.band ? BAND_CLASS[entry.band] : ''}>{entry.score}</span>
                  {worst ? <span className="text-ink-600">{worst.reason}</span> : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Panel>
  );
}
